import React from 'react';
import { IconBuildingStore, IconUser } from '@tabler/icons-react';
import { Radio } from '@planne-software/uni/mui/material';

import { useTranslate } from '@src/core/hooks';
import { IDENTITY_TYPE, type IdentityType } from '@src/domains/customers';

import {
  IdentityTypeCard,
  IdentityTypeCardHeader,
  IdentityTypeCardIcon,
  IdentityTypeCardRadio,
  IdentityTypeCardSubtitle,
  IdentityTypeCardText,
  IdentityTypeCardTitle,
  IdentityTypeGrid,
} from '../styles';

const OPTIONS = [
  {
    value: IDENTITY_TYPE.CPF,
    titleKey: 'checkout_auth_signup_person_title',
    subtitleKey: 'checkout_auth_signup_person_subtitle',
    icon: IconUser,
  },
  {
    value: IDENTITY_TYPE.CNPJ,
    titleKey: 'checkout_auth_signup_company_title',
    subtitleKey: 'checkout_auth_signup_company_subtitle',
    icon: IconBuildingStore,
  },
] as const;

type IdentityTypeSelectorProps = {
  value: IdentityType;
  onChange: (value: IdentityType) => void;
  disabled?: boolean;
};

export const IdentityTypeSelector = (props: IdentityTypeSelectorProps) => {
  const { value, onChange, disabled } = props;
  const { i18n } = useTranslate();

  return (
    <IdentityTypeGrid role='radiogroup' aria-label={i18n('checkout_auth_signup_identity_type_label')}>
      {OPTIONS.map((option) => {
        const selected = value === option.value;
        const Icon = option.icon;

        return (
          <IdentityTypeCard
            key={option.value}
            data-testid={`signup-identity-type-${option.value}`}
            data-selected={String(selected)}
            onClick={() => !disabled && onChange(option.value)}
          >
            <IdentityTypeCardHeader>
              <IdentityTypeCardIcon data-selected={String(selected)}>
                <Icon size={24} aria-hidden />
              </IdentityTypeCardIcon>

              <IdentityTypeCardRadio>
                <Radio
                  name='identityType'
                  value={option.value}
                  checked={selected}
                  disabled={disabled}
                  color='primary'
                  onChange={() => onChange(option.value)}
                />
              </IdentityTypeCardRadio>
            </IdentityTypeCardHeader>

            <IdentityTypeCardText>
              <IdentityTypeCardTitle data-selected={String(selected)}>{i18n(option.titleKey)}</IdentityTypeCardTitle>
              <IdentityTypeCardSubtitle data-selected={String(selected)}>
                {i18n(option.subtitleKey)}
              </IdentityTypeCardSubtitle>
            </IdentityTypeCardText>
          </IdentityTypeCard>
        );
      })}
    </IdentityTypeGrid>
  );
};

export default IdentityTypeSelector;
